import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  TableSortLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Create as CreateWorkOrderIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { formatDateWithTimezone, formatDateTimeWithTimezone } from '../utils/timezoneUtils';
import { useAMPSchedules } from './amp/hooks/useAMPSchedules';
import { useAMPSchedulePermissions } from './amp/hooks/useAMPSchedulePermissions';
import CreateEditAMPScheduleDialog from './amp/CreateEditAMPScheduleDialog';
import CreateIntimationFromAMPScheduleDialog from './amp/CreateIntimationFromAMPScheduleDialog';
import {
  isOneTimeAmpSchedule,
  isAmpAssemblyNotApplicable,
  SCHEDULE_FILTER_LABELS
} from './maintenance/utils/maintenanceUtils';

// Action buttons component to avoid arrow functions in map
const ScheduleActionButtons = React.memo(({ schedule, permissions, onEdit, onCreateIntimation, onDelete }) => {
  const handleEdit = React.useCallback(() => {
    onEdit(schedule);
  }, [onEdit, schedule]);

  const handleCreateIntimation = React.useCallback(() => {
    onCreateIntimation(schedule);
  }, [onCreateIntimation, schedule]);

  const handleDelete = React.useCallback(() => {
    onDelete(schedule);
  }, [onDelete, schedule]);

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="View Details">
        <IconButton size="small" onClick={handleEdit}>
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      {permissions.canEditAMPSchedule && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={handleEdit}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {permissions.canCreateWorkOrder && (
        <Tooltip title="Create Intimation">
          <IconButton
            size="small"
            color="primary"
            onClick={handleCreateIntimation}
          >
            <CreateWorkOrderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {permissions.canDeleteAMPSchedule && (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={handleDelete}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

ScheduleActionButtons.displayName = 'ScheduleActionButtons';

const INSPECTION_CATEGORY_OPTIONS = [
  { value: 'PHASE_INSPECTION', label: 'Phase Inspection' },
  { value: 'OUT_OF_PHASE_INSPECTION', label: 'Out of phase Inspection' },
  { value: 'LUBRICATION_INSPECTION', label: 'Lubrication Inspection' },
  { value: 'CONDITIONAL_INSPECTION', label: 'Conditional Inspection' },
  { value: 'ONE_TIME_INSPECTIONS', label: 'One Time Inspections' },
  { value: 'CORROSION_PREVENTION_AND_CONTROL', label: 'Corrosion Prevention and Control' },
];

const inspectionCategoryLabel = (value) =>
  INSPECTION_CATEGORY_OPTIONS.find(o => o.value === value)?.label ?? value ?? '—';

const formatAssemblyLabel = (asm) => {
  if (!asm) return '';
  if (typeof asm !== 'object') return String(asm);
  const typeName = asm.typeName || asm.type?.name || 'Assembly';
  const sn = asm.serialNumber || 'N/A';
  return `${typeName} – SN: ${sn}`;
};

const scheduleMatchesAssemblyFilter = (schedule, assemblyFilter) => {
  if (!assemblyFilter || assemblyFilter === 'ALL') return true;
  if (assemblyFilter === 'NOT_APPLICABLE') return schedule.isNA === true;
  const assemblyIds = (schedule.assemblies || []).map((a) => (a._id || a)?.toString());
  return assemblyIds.includes(String(assemblyFilter));
};

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AMPSchedules = () => {
  const [selectedAircraftId, setSelectedAircraftId] = useState('');
  const [activeTab, setActiveTab] = useState(INSPECTION_CATEGORY_OPTIONS[0].value);
  const [searchTerm, setSearchTerm] = useState('');
  const [maintenanceTypeFilter, setMaintenanceTypeFilter] = useState('ALL');
  const [naFilter, setNaFilter] = useState('ALL');
  const [assemblyFilter, setAssemblyFilter] = useState('ALL');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [createWOOpen, setCreateWOOpen] = useState(false);
  const [selectedScheduleForWO, setSelectedScheduleForWO] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('remainingLive');
  const [sortOrder, setSortOrder] = useState('asc');
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';

  const { user } = useAuth();
  const { showToast } = useToast();
  const { ampSchedules, aircraft, assemblies, loading, fetchAMPSchedules, fetchAssemblies } = useAMPSchedules();
  const permissions = useAMPSchedulePermissions(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const processedScheduleIdFromUrlRef = React.useRef(null);

  // Deep link: /amp-schedules?scheduleId=… (e.g. Maintenance → Upcoming → View)
  React.useEffect(() => {
    const scheduleId = searchParams.get('scheduleId');
    if (!scheduleId) {
      processedScheduleIdFromUrlRef.current = null;
      return;
    }
    if (loading) return;
    if (processedScheduleIdFromUrlRef.current === scheduleId) return;
    processedScheduleIdFromUrlRef.current = scheduleId;

    let cancelled = false;

    (async () => {
      try {
        let schedule = ampSchedules.find((s) => String(s._id || s) === String(scheduleId));
        if (!schedule) {
          const res = await axios.get(`${API_URL}/amp-schedules/${scheduleId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          schedule = res.data;
        }
        if (cancelled || !schedule) return;

        const aid = (schedule.aircraftId?._id || schedule.aircraftId)?.toString() || '';
        if (aid) setSelectedAircraftId(aid);
        const cat = schedule.inspectionCategory;
        if (cat && INSPECTION_CATEGORY_OPTIONS.some((o) => o.value === cat)) {
          setActiveTab(cat);
        }
        setSelectedSchedule(schedule);
        setOpenDialog(true);

        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('scheduleId');
            return next;
          },
          { replace: true }
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) showToast('Could not load AMP schedule', 'error');
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('scheduleId');
            return next;
          },
          { replace: true }
        );
      }
    })();

    return () => {
      cancelled = true;
      processedScheduleIdFromUrlRef.current = null;
    };
  }, [searchParams, ampSchedules, loading, showToast, setSearchParams]);

  // Fetch all schedules when no aircraft selected (for dashboard); fetch by aircraft when selected
  React.useEffect(() => {
    const controller = new AbortController();
    if (selectedAircraftId) {
      fetchAMPSchedules({ aircraftId: selectedAircraftId }, controller.signal);
    } else {
      fetchAMPSchedules({}, controller.signal);
    }
    return () => controller.abort();
  }, [selectedAircraftId, fetchAMPSchedules]);

  React.useEffect(() => {
    fetchAssemblies(selectedAircraftId || null);
  }, [selectedAircraftId, fetchAssemblies]);

  // Schedules for the selected aircraft only (all categories)
  const aircraftSchedules = React.useMemo(
    () => ampSchedules.filter(s => (s.aircraftId?._id || s.aircraftId)?.toString() === selectedAircraftId),
    [ampSchedules, selectedAircraftId]
  );

  // Per-tab: schedules for active inspection category, then filtered by search/maintenance
  const schedulesForActiveTab = React.useMemo(() => {
    const byCategory = aircraftSchedules.filter(s => s.inspectionCategory === activeTab);
    return byCategory.filter(schedule => {
      const matchesSearch = !searchTerm ||
        schedule.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.aircraftId?.registration?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMaintenanceType = maintenanceTypeFilter === 'ALL' || schedule.maintenanceType === maintenanceTypeFilter;
      const matchesScheduleFilter =
        naFilter === 'ALL' ||
        (naFilter === 'NOT_APPLICABLE' && isAmpAssemblyNotApplicable({ ...schedule, source: 'AMP_SCHEDULE' })) ||
        (naFilter === 'EXCLUDE_NOT_APPLICABLE' && !isAmpAssemblyNotApplicable({ ...schedule, source: 'AMP_SCHEDULE' })) ||
        (naFilter === 'ONE_TIME' && isOneTimeAmpSchedule(schedule));
      const matchesAssembly = scheduleMatchesAssemblyFilter(schedule, assemblyFilter);
      return matchesSearch && matchesMaintenanceType && matchesScheduleFilter && matchesAssembly;
    });
  }, [aircraftSchedules, activeTab, searchTerm, maintenanceTypeFilter, naFilter, assemblyFilter]);

  // Count per category for tab labels
  const countByCategory = React.useMemo(() => {
    const counts = {};
    INSPECTION_CATEGORY_OPTIONS.forEach(o => { counts[o.value] = 0; });
    aircraftSchedules.forEach(s => {
      if (counts[s.inspectionCategory] !== undefined) counts[s.inspectionCategory]++;
    });
    return counts;
  }, [aircraftSchedules]);

  // Dashboard summary when no aircraft selected: per-aircraft counts, category totals, and extra metrics
  const dashboardSummary = React.useMemo(() => {
    const categoryTotals = {};
    INSPECTION_CATEGORY_OPTIONS.forEach(o => { categoryTotals[o.value] = 0; });
    const byAircraftMap = new Map();
    const maintenanceTypeCounts = { LINE_INSPECTION: 0, BASE_INSPECTION: 0, ANY: 0 };
    let oneTimeCount = 0;
    let recurringCount = 0;
    let totalAmpChecks = 0;
    let schedulesWithWorkOrders = 0;
    let overdueCount = 0;
    let dueSoonCount = 0;
    const recurrenceBasisCounts = { HOURS: 0, CYCLES: 0, LANDINGS: 0, DAYS: 0, MONTHS: 0, YEARS: 0, ON_CONDITION: 0 };
    const now = new Date();
    const dueSoonEnd = new Date(now);
    dueSoonEnd.setDate(dueSoonEnd.getDate() + 30);

    ampSchedules.forEach(s => {
      const aid = (s.aircraftId?._id || s.aircraftId)?.toString();
      const reg = s.aircraftId?.registration || 'Unknown';
      if (!byAircraftMap.has(aid)) {
        const counts = {};
        INSPECTION_CATEGORY_OPTIONS.forEach(o => { counts[o.value] = 0; });
        byAircraftMap.set(aid, { aircraftId: aid, registration: reg, counts, total: 0 });
      }
      const row = byAircraftMap.get(aid);
      const cat = s.inspectionCategory;
      if (row.counts[cat] !== undefined) {
        row.counts[cat]++;
        row.total++;
        categoryTotals[cat]++;
      }
      if (maintenanceTypeCounts[s.maintenanceType] !== undefined) maintenanceTypeCounts[s.maintenanceType]++;
      if (s.recurrenceType === 'ONE_TIME') oneTimeCount++;
      else if (s.recurrenceType === 'RECURRING') recurringCount++;
      totalAmpChecks += s.ampChecks?.length ?? 0;
      if (s.workOrders?.length > 0) schedulesWithWorkOrders++;
      if (s.recurrenceType === 'RECURRING' && s.recurrenceBasedOn && recurrenceBasisCounts[s.recurrenceBasedOn] !== undefined) {
        recurrenceBasisCounts[s.recurrenceBasedOn]++;
      }
      const due = s.nextDueDate ? new Date(s.nextDueDate) : null;
      if (due) {
        if (due < now) overdueCount++;
        else if (due <= dueSoonEnd) dueSoonCount++;
      }
    });

    const byAircraft = Array.from(byAircraftMap.values()).sort((a, b) =>
      (a.registration || '').localeCompare(b.registration || '')
    );
    const grandTotal = Object.values(categoryTotals).reduce((sum, n) => sum + n, 0);
    const aircraftIdsWithSchedules = new Set(byAircraft.map((r) => r.aircraftId));
    const aircraftWithNoSchedules = aircraft.filter(
      (ac) => !aircraftIdsWithSchedules.has((ac._id || ac).toString())
    ).length;
    const avgSchedulesPerAircraft =
      byAircraft.length > 0 ? (grandTotal / byAircraft.length).toFixed(1) : '0';

    return {
      byAircraft,
      categoryTotals,
      grandTotal,
      maintenanceTypeCounts,
      oneTimeCount,
      recurringCount,
      totalAmpChecks,
      schedulesWithWorkOrders,
      overdueCount,
      dueSoonCount,
      recurrenceBasisCounts,
      aircraftWithNoSchedules,
      avgSchedulesPerAircraft
    };
  }, [ampSchedules, aircraft]);

  const handleCreate = useCallback(() => {
    setSelectedSchedule(null);
    setOpenDialog(true);
  }, []);

  const handleEdit = useCallback((schedule) => {
    setSelectedSchedule(schedule);
    setOpenDialog(true);
  }, []);

  const handleDelete = useCallback((schedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      await axios.delete(`/api/amp-schedules/${scheduleToDelete._id}`);
      showToast('AMP schedule deleted successfully', 'success');
      fetchAMPSchedules();
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error('Error deleting AMP schedule:', error);
      showToast(error.response?.data?.message || 'Failed to delete AMP schedule', 'error');
    }
  }, [scheduleToDelete, showToast, fetchAMPSchedules]);

  const handleCreateIntimation = useCallback((schedule) => {
    setSelectedScheduleForWO(schedule);
    setCreateWOOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
    setSelectedSchedule(null);
  }, []);

  const handleDialogSuccess = useCallback(() => {
    fetchAMPSchedules();
    handleDialogClose();
  }, [fetchAMPSchedules, handleDialogClose]);

  const handleProofDocumentsChange = useCallback((updated) => {
    setSelectedSchedule(updated);
    fetchAMPSchedules();
  }, [fetchAMPSchedules]);


  const handleCreateIntimationSuccess = useCallback(() => {
    setCreateWOOpen(false);
    setSelectedScheduleForWO(null);
    fetchAMPSchedules();
  }, [fetchAMPSchedules]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setMaintenanceTypeFilter('ALL');
    setNaFilter('ALL');
    setAssemblyFilter('ALL');
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleCloseCreateWODialog = useCallback(() => {
    setCreateWOOpen(false);
    setSelectedScheduleForWO(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleAircraftChange = useCallback((e) => {
    setSelectedAircraftId(e.target.value || '');
    setActiveTab(INSPECTION_CATEGORY_OPTIONS[0].value);
    setSearchTerm('');
    setMaintenanceTypeFilter('ALL');
    setNaFilter('ALL');
    setAssemblyFilter('ALL');
  }, []);

  const handleTabChange = useCallback((_, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleMaintenanceTypeFilterChange = useCallback((e) => {
    setMaintenanceTypeFilter(e.target.value);
  }, []);

  const handleNaFilterChange = useCallback((e) => {
    setNaFilter(e.target.value);
  }, []);

  const handleAssemblyFilterChange = useCallback((e) => {
    setAssemblyFilter(e.target.value);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);

  const handleSortRequest = useCallback((columnId) => {
    const isAsc = sortBy === columnId && sortOrder === 'asc';
    setSortBy(columnId);
    setSortOrder(isAsc ? 'desc' : 'asc');
  }, [sortBy, sortOrder]);

  const hasActiveFilters = searchTerm || maintenanceTypeFilter !== 'ALL' || naFilter !== 'ALL' || assemblyFilter !== 'ALL';

  const getSchedulesList = (schedule) => {
    if (schedule.schedules && schedule.schedules.length > 0) {
      return schedule.schedules.filter(s => s.recurrenceBasedOn && s.recurrenceValue != null).map(s => ({
        ...s,
        matchType: s.matchType || 'EXACTLY',
        recurrenceValue2: s.recurrenceValue2 ?? null
      }));
    }
    if (schedule.recurrenceBasedOn && schedule.recurrenceValue != null) {
      return [{
        recurrenceBasedOn: schedule.recurrenceBasedOn,
        recurrenceValue: schedule.recurrenceValue,
        matchType: schedule.matchType || 'EXACTLY',
        recurrenceValue2: schedule.recurrenceValue2 ?? null
      }];
    }
    return [];
  };

  /** Display AMP schedule number (stored or derived from _id for legacy) */
  const getAmpScheduleNumber = (schedule) => {
    if (!schedule) return '—';
    if (schedule.ampScheduleNumber) return schedule.ampScheduleNumber;
    const id = schedule._id;
    if (!id) return '—';
    return 'AMP-' + id.toString().slice(-8).toLowerCase();
  };

  /** Format decimal lastDoneValue (HOURS) as "950h 30m" for display. */
  const formatLastDoneHoursDisplay = (value) => {
    if (value == null || value === '') return '';
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) return '';
    const hours = Math.floor(n);
    let minutes = Math.round((n % 1) * 60);
    if (minutes >= 60) minutes = 59;
    if (minutes < 0) minutes = 0;
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  };

  /** Format last performed at for display/export: one string per schedule (e.g. "100 Hours: 950h 30m; 12 Months: 2025-01-15") */
  const formatLastPerformedAt = (schedule) => {
    const list = getSchedulesList(schedule);
    const parts = list
      .filter(s => s.lastDoneValue != null && s.lastDoneValue !== '')
      .map(s => {
        const label = s.recurrenceBasedOn === 'ON_CONDITION'
          ? 'On Condition'
          : (s.matchType === 'BETWEEN' && s.recurrenceValue2 != null
            ? `${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue)}–${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue2)} ${s.recurrenceBasedOn}`
            : `${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue)} ${s.recurrenceBasedOn}`);
        let val = s.lastDoneValue;
        if (s.recurrenceBasedOn === 'HOURS' && typeof val === 'number') {
          val = formatLastDoneHoursDisplay(val);
        } else if (['DAYS', 'MONTHS', 'YEARS', 'ON_CONDITION'].includes(s.recurrenceBasedOn) && (typeof val === 'string' || val instanceof Date)) {
          const d = new Date(val);
          val = Number.isNaN(d.getTime()) ? val : d.toISOString().slice(0, 10);
        }
        const note = s.lastDoneNote && String(s.lastDoneNote).trim() ? ` (${String(s.lastDoneNote).trim()})` : '';
        return `${label}: ${val}${note}`;
      });
    return parts.join('; ') || '';
  };

  /** Format a single schedule value for display: HOURS as integer hours, CYCLES with decimals, ON_CONDITION as text, others integer */
  const formatScheduleValue = (basedOn, value) => {
    if (value == null) return '';
    if (basedOn === 'ON_CONDITION') return String(value).trim() || 'Based on condition';
    if (basedOn === 'HOURS') {
      const v = Number(value);
      if (Number.isNaN(v)) return '';
      return String(Math.max(0, Math.floor(v)));
    }
    if (basedOn === 'CYCLES') return Number(value);
    return Math.floor(Number(value));
  };

  const formatRecurrence = (schedule) => {
    if (
      !schedule.recurrenceType ||
      schedule.recurrenceType === 'ONE_TIME' ||
      schedule.inspectionCategory === 'ONE_TIME_INSPECTIONS'
    ) {
      return 'One-time';
    }
    const list = getSchedulesList(schedule);
    if (list.length === 0) return 'N/A';
    if (list.length === 1) {
      const s0 = list[0];
      if (s0.recurrenceBasedOn === 'ON_CONDITION') {
        return `On condition: ${formatScheduleValue(s0.recurrenceBasedOn, s0.recurrenceValue)}`;
      }
      if ((s0.matchType || 'EXACTLY') === 'BETWEEN' && s0.recurrenceValue2 != null) {
        return `Every ${formatScheduleValue(s0.recurrenceBasedOn, s0.recurrenceValue)}–${formatScheduleValue(s0.recurrenceBasedOn, s0.recurrenceValue2)} ${s0.recurrenceBasedOn}`;
      }
      return `Every ${formatScheduleValue(s0.recurrenceBasedOn, s0.recurrenceValue)} ${s0.recurrenceBasedOn}`;
    }
    const parts = list.map(s => {
      if (s.recurrenceBasedOn === 'ON_CONDITION') {
        return `On condition: ${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue)}`;
      }
      if ((s.matchType || 'EXACTLY') === 'BETWEEN' && s.recurrenceValue2 != null) {
        return `Every ${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue)}–${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue2)} ${s.recurrenceBasedOn}`;
      }
      return `Every ${formatScheduleValue(s.recurrenceBasedOn, s.recurrenceValue)} ${s.recurrenceBasedOn}`;
    });
    const suffix = schedule.scheduleCombinationMode === 'ALL_MUST_BE_MET'
      ? ' (all must be met)'
      : ' (whichever occurs first)';
    return parts.join(' or ') + suffix;
  };

  const formatAssemblies = (schedule) => {
    if (schedule.isNA) {
      return 'NA';
    }
    if (schedule.assemblies && schedule.assemblies.length > 0) {
      return schedule.assemblies.map(a => a.typeName || 'Unknown').join(', ');
    }
    return 'None';
  };

  const parseRemainingSortValue = (text) => {
    if (text == null) return null;
    const raw = String(text).trim();
    if (!raw || raw === '—') return null;

    const hm = raw.match(/(-?\d+(?:\.\d+)?)\s*h(?:\s*(\d+)\s*m)?/i);
    if (hm) {
      const h = Number(hm[1]);
      const m = hm[2] ? Number(hm[2]) : 0;
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        const sign = h < 0 ? -1 : 1;
        return sign * ((Math.abs(h) * 60) + m);
      }
    }

    const withUnit = raw.match(/(-?\d+(?:\.\d+)?)\s*(hours?|cycles?|landings?|days?|months?|years?)/i);
    if (withUnit) {
      const n = Number(withUnit[1]);
      if (!Number.isNaN(n)) return n;
    }

    const firstNumber = raw.match(/-?\d+(?:\.\d+)?/);
    if (firstNumber) {
      const n = Number(firstNumber[0]);
      if (!Number.isNaN(n)) return n;
    }
    return null;
  };

  const getOrderedScheduleEntryIndexes = (schedule) => {
    const nextEntries = Array.isArray(schedule?.nextDueAtEntries) ? schedule.nextDueAtEntries : [];
    const remainingEntries = Array.isArray(schedule?.remainingLiveAtEntries) ? schedule.remainingLiveAtEntries : [];
    const totalEntries = Math.max(nextEntries.length, remainingEntries.length);
    if (totalEntries <= 0) return [];

    const candidates = Array.from({ length: totalEntries }, (_, idx) => {
      const dueEntry = nextEntries[idx];
      const remEntry = remainingEntries[idx];
      const primary = dueEntry?.nextDueDate ? new Date(dueEntry.nextDueDate).getTime() : null;
      const low = dueEntry?.nextDueDateLow ? new Date(dueEntry.nextDueDateLow).getTime() : null;
      const high = dueEntry?.nextDueDateHigh ? new Date(dueEntry.nextDueDateHigh).getTime() : null;
      const dueTs = [primary, low, high].find((ts) => Number.isFinite(ts));
      const remainingSortValue = parseRemainingSortValue(remEntry?.text);
      return { idx, dueTs, remainingSortValue };
    });

    candidates.sort((a, b) => {
      const aHasDue = Number.isFinite(a.dueTs);
      const bHasDue = Number.isFinite(b.dueTs);
      if (aHasDue && bHasDue && a.dueTs !== b.dueTs) return a.dueTs - b.dueTs;
      if (aHasDue !== bHasDue) return aHasDue ? -1 : 1;

      const aHasRemaining = a.remainingSortValue != null;
      const bHasRemaining = b.remainingSortValue != null;
      if (aHasRemaining && bHasRemaining && a.remainingSortValue !== b.remainingSortValue) {
        return a.remainingSortValue - b.remainingSortValue;
      }
      if (aHasRemaining !== bHasRemaining) return aHasRemaining ? -1 : 1;
      return a.idx - b.idx;
    });

    return candidates.map((c) => c.idx);
  };

  const getOrderedNextDueEntries = (schedule) => {
    const entries = schedule?.nextDueAtEntries || [];
    const orderedIndexes = getOrderedScheduleEntryIndexes(schedule);
    if (!orderedIndexes.length) return entries;
    return orderedIndexes
      .map((idx) => entries[idx])
      .filter(Boolean);
  };

  const getOrderedRemainingEntries = (schedule) => {
    const entries = schedule?.remainingLiveAtEntries || [];
    const orderedIndexes = getOrderedScheduleEntryIndexes(schedule);
    if (!orderedIndexes.length) return entries;
    return orderedIndexes
      .map((idx) => entries[idx])
      .filter(Boolean);
  };

  /** Sorted schedules for the active tab (by current sort column and order) */
  const sortedSchedulesForActiveTab = React.useMemo(() => {
    const list = [...schedulesForActiveTab];
    const mult = sortOrder === 'asc' ? 1 : -1;
    const cmp = (a, b) => {
      let va; let vb;
      switch (sortBy) {
        case 'ampNumber':
          va = getAmpScheduleNumber(a);
          vb = getAmpScheduleNumber(b);
          return mult * (String(va || '').localeCompare(String(vb || '')));
        case 'title':
          va = (a.title || '').toLowerCase();
          vb = (b.title || '').toLowerCase();
          return mult * va.localeCompare(vb);
        case 'description':
          va = (a.description || '').toLowerCase();
          vb = (b.description || '').toLowerCase();
          return mult * va.localeCompare(vb);
        case 'aircraft':
          va = (a.aircraftId?.registration || 'Unknown').toLowerCase();
          vb = (b.aircraftId?.registration || 'Unknown').toLowerCase();
          return mult * va.localeCompare(vb);
        case 'maintenanceType':
          va = a.maintenanceType || '';
          vb = b.maintenanceType || '';
          return mult * va.localeCompare(vb);
        case 'inspectionCategory':
          va = inspectionCategoryLabel(a.inspectionCategory);
          vb = inspectionCategoryLabel(b.inspectionCategory);
          return mult * (va || '').localeCompare(vb || '');
        case 'assemblies':
          va = formatAssemblies(a);
          vb = formatAssemblies(b);
          return mult * (va || '').localeCompare(vb || '');
        case 'recurrence':
          va = formatRecurrence(a);
          vb = formatRecurrence(b);
          return mult * (va || '').localeCompare(vb || '');
        case 'nextDueDate': {
          va = a.nextDueDate ? new Date(a.nextDueDate).getTime() : 0;
          vb = b.nextDueDate ? new Date(b.nextDueDate).getTime() : 0;
          return mult * (va - vb);
        }
        case 'remainingLive': {
          const aTop = getOrderedRemainingEntries(a)[0] || null;
          const bTop = getOrderedRemainingEntries(b)[0] || null;
          va = parseRemainingSortValue(aTop?.text);
          vb = parseRemainingSortValue(bTop?.text);
          if (va != null && vb != null) return mult * (va - vb);
          if (va == null && vb == null) {
            const aText = aTop?.text || '';
            const bText = bTop?.text || '';
            return mult * aText.localeCompare(bText);
          }
          return va == null ? 1 : -1;
        }
        case 'numChecks': {
          va = a.ampChecks?.length ?? 0;
          vb = b.ampChecks?.length ?? 0;
          return mult * (va - vb);
        }
        case 'numWorkOrders': {
          va = a.workOrders?.length ?? 0;
          vb = b.workOrders?.length ?? 0;
          return mult * (va - vb);
        }
        default:
          return 0;
      }
    };
    list.sort(cmp);
    return list;
  }, [schedulesForActiveTab, sortBy, sortOrder]);

  /** Escape a value for CSV (quotes and commas) */
  const escapeCsv = (value) => {
    if (value == null || value === '') return '';
    const str = String(value);
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const handleExportCsv = useCallback(() => {
    const schedulesToExport = schedulesForActiveTab;
    const headers = [
      'AMP #',
      'ID',
      'Title',
      'Description',
      'Aircraft Registration',
      'Maintenance Type',
      'Inspection Category',
      'Is NA',
      'Assemblies',
      'Recurrence Type',
      'Recurrence Based On',
      'Recurrence Value',
      'Recurrence (Formatted)',
      'Next Due At',
      'Remaining (Live)',
      'Last Performed At',
      'Last Work Order Created At',
      'Number of Checks',
      'AMP Checks (Details)',
      'Number of Work Orders',
      'Created At',
      'Updated At'
    ];
    const rows = schedulesToExport.map((schedule) => {
      const recurrenceFormatted = formatRecurrence(schedule);
      const assembliesFormatted = formatAssemblies(schedule);
      const maintenanceTypeLabel = schedule.maintenanceType === 'LINE_INSPECTION' ? 'Line Inspection' : schedule.maintenanceType === 'BASE_INSPECTION' ? 'Base Inspection' : schedule.maintenanceType || '';
      const ampChecksDetail = (schedule.ampChecks && schedule.ampChecks.length > 0)
        ? schedule.ampChecks
            .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
            .map((c) => `${c.sequenceNumber ?? ''}: ${c.sequenceLabel || ''} - ${(c.description || '').replace(/\r?\n/g, ' ')}`.trim())
            .join('; ')
        : '';
      return [
        getAmpScheduleNumber(schedule),
        schedule._id,
        schedule.title,
        schedule.description ?? '',
        schedule.aircraftId?.registration ?? 'Unknown',
        maintenanceTypeLabel,
        inspectionCategoryLabel(schedule.inspectionCategory),
        schedule.isNA ? 'Yes' : 'No',
        assembliesFormatted,
        schedule.recurrenceType ?? '',
        schedule.recurrenceBasedOn ?? '',
        schedule.recurrenceValue ?? '',
        recurrenceFormatted,
        (schedule.nextDueAtEntries || []).map(e => e.tooltip ? `${e.text} [${e.tooltip}]` : e.text).join('; '),
        (schedule.remainingLiveAtEntries || []).map(e => e.tooltip ? `${e.text} [${e.tooltip}]` : e.text).join('; '),
        formatLastPerformedAt(schedule),
        schedule.lastWorkOrderCreatedAt ? new Date(schedule.lastWorkOrderCreatedAt).toISOString() : '',
        schedule.ampChecks?.length ?? 0,
        ampChecksDetail,
        schedule.workOrders?.length ?? 0,
        schedule.createdAt ? new Date(schedule.createdAt).toISOString() : '',
        schedule.updatedAt ? new Date(schedule.updatedAt).toISOString() : ''
      ].map(escapeCsv);
    });
    const csvContent = [headers.map(escapeCsv).join(','), ...rows.map((row) => row.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const categorySlug = (activeTab || '').replace(/_/g, '-').toLowerCase();
    link.setAttribute('download', `amp-schedules-${categorySlug}-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('AMP schedules exported to CSV', 'success');
  }, [schedulesForActiveTab, activeTab, showToast]);

  const lockCreateContext = !selectedSchedule && !!selectedAircraftId;
  const aircraftSelected = !!selectedAircraftId;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Aircraft Maintenance Program (AMP) Schedules</Typography>
      </Box>

      {/* Aircraft selection (required first) */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Aircraft *</InputLabel>
          <Select
            value={selectedAircraftId}
            label="Aircraft *"
            onChange={handleAircraftChange}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select an aircraft...</em>
            </MenuItem>
            {aircraft.map(ac => (
              <MenuItem key={ac._id} value={ac._id}>
                {ac.registration}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {!aircraftSelected ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
            Summary dashboard (no aircraft selected)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an aircraft above to view and manage schedules by inspection category, or review the summary below.
          </Typography>
          {loading ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Loading summary...</Typography>
            </Paper>
          ) : (
            <>
              {/* First row: core totals + maintenance type + one-time/recurring + checks + work orders */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Paper sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">{dashboardSummary.grandTotal}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Schedules</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                  <Typography variant="h4" color="text.primary">{dashboardSummary.byAircraft.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Aircraft</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.maintenanceTypeCounts.LINE_INSPECTION ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Line</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.maintenanceTypeCounts.BASE_INSPECTION ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Base</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.maintenanceTypeCounts.ANY ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Any</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.oneTimeCount}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>One-time</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.recurringCount}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Recurring</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.totalAmpChecks}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Total AMP Checks</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.schedulesWithWorkOrders}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>With Work Orders</Typography>
                </Paper>
              </Box>
              {/* Second row: overdue, due soon, aircraft with no schedules, avg per aircraft */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color={dashboardSummary.overdueCount > 0 ? 'error' : 'text.primary'}>{dashboardSummary.overdueCount}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Overdue</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.dueSoonCount}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Due in 30 days</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.aircraftWithNoSchedules}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Aircraft w/ no schedules</Typography>
                </Paper>
                <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                  <Typography variant="h5" color="text.primary">{dashboardSummary.avgSchedulesPerAircraft}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>Avg per aircraft</Typography>
                </Paper>
              </Box>
              {/* Recurrence basis (recurring only) */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Recurring by basis:</Typography>
                {['HOURS', 'CYCLES', 'LANDINGS', 'DAYS', 'MONTHS', 'YEARS', 'ON_CONDITION'].map((basis) => (
                  <Chip
                    key={basis}
                    size="small"
                    label={`${basis}: ${dashboardSummary.recurrenceBasisCounts[basis] ?? 0}`}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </Box>
              {/* Category totals row */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {INSPECTION_CATEGORY_OPTIONS.map((o) => (
                  <Paper key={o.value} sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
                    <Typography variant="h5" color="text.primary">{dashboardSummary.categoryTotals[o.value] ?? 0}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>{o.label}</Typography>
                  </Paper>
                ))}
              </Box>
              <Paper sx={{ overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Aircraft</TableCell>
                        {INSPECTION_CATEGORY_OPTIONS.map((o) => (
                          <TableCell key={o.value} align="right" sx={{ fontWeight: 600 }}>{o.label}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardSummary.byAircraft.map((row) => (
                        <TableRow key={row.aircraftId} hover>
                          <TableCell>{row.registration}</TableCell>
                          {INSPECTION_CATEGORY_OPTIONS.map((o) => (
                            <TableCell key={o.value} align="right">{row.counts[o.value] || 0}</TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{row.total}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Total (all aircraft)</TableCell>
                        {INSPECTION_CATEGORY_OPTIONS.map((o) => (
                          <TableCell key={o.value} align="right" sx={{ fontWeight: 600 }}>{dashboardSummary.categoryTotals[o.value] ?? 0}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{dashboardSummary.grandTotal}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </Box>
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {INSPECTION_CATEGORY_OPTIONS.map((o) => (
              <Tab
                key={o.value}
                value={o.value}
                label={`${o.label} (${countByCategory[o.value] ?? 0})`}
              />
            ))}
          </Tabs>

          {INSPECTION_CATEGORY_OPTIONS.map((cat) => (
            <Box
              key={cat.value}
              role="tabpanel"
              hidden={activeTab !== cat.value}
              id={`amp-tabpanel-${cat.value}`}
              aria-labelledby={`amp-tab-${cat.value}`}
            >
              {activeTab === cat.value && (
                <Box sx={{ pt: 1 }}>
                  {/* Per-tab: filters, Create, Export */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Search by title..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: searchTerm && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={handleClearSearch}>
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{ minWidth: 220 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Maintenance Type</InputLabel>
                        <Select
                          value={maintenanceTypeFilter}
                          label="Maintenance Type"
                          onChange={handleMaintenanceTypeFilterChange}
                        >
                          <MenuItem value="ALL">All Types</MenuItem>
                          <MenuItem value="LINE_INSPECTION">Line Inspection</MenuItem>
                          <MenuItem value="BASE_INSPECTION">Base Inspection</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Schedule filter</InputLabel>
                        <Select
                          value={naFilter}
                          label="Schedule filter"
                          onChange={handleNaFilterChange}
                          renderValue={(v) => SCHEDULE_FILTER_LABELS[v] || v}
                        >
                          <MenuItem value="ALL">All</MenuItem>
                          <MenuItem value="NOT_APPLICABLE">Not Applicable</MenuItem>
                          <MenuItem value="EXCLUDE_NOT_APPLICABLE">Exclude Not Applicable</MenuItem>
                          <MenuItem value="ONE_TIME">One time</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Assembly</InputLabel>
                        <Select
                          value={assemblyFilter}
                          label="Assembly"
                          onChange={handleAssemblyFilterChange}
                          disabled={!selectedAircraftId}
                        >
                          <MenuItem value="ALL">All Assemblies</MenuItem>
                          <MenuItem value="NOT_APPLICABLE">Not Applicable (NA)</MenuItem>
                          {assemblies.map((asm) => (
                            <MenuItem key={asm._id} value={asm._id}>
                              {formatAssemblyLabel(asm)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {hasActiveFilters && (
                        <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
                          Clear Filters
                        </Button>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportCsv}
                        disabled={loading || schedulesForActiveTab.length === 0}
                      >
                        Export to CSV
                      </Button>
                      {permissions.canCreateAMPSchedule && (
                        <Button variant="contained" size="small" startIcon={<Add />} onClick={handleCreate}>
                          Create AMP Schedule
                        </Button>
                      )}
                    </Box>
                    {hasActiveFilters && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Showing {schedulesForActiveTab.length} of {aircraftSchedules.filter(s => s.inspectionCategory === activeTab).length} in this category
                      </Typography>
                    )}
                  </Paper>

                  {/* Table for this tab */}
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sortDirection={sortBy === 'ampNumber' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'ampNumber'}
                              direction={sortBy === 'ampNumber' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('ampNumber')}
                            >
                              AMP #
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'title' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'title'}
                              direction={sortBy === 'title' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('title')}
                            >
                              Title
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'description' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'description'}
                              direction={sortBy === 'description' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('description')}
                            >
                              Description
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'aircraft' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'aircraft'}
                              direction={sortBy === 'aircraft' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('aircraft')}
                            >
                              Aircraft
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'maintenanceType' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'maintenanceType'}
                              direction={sortBy === 'maintenanceType' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('maintenanceType')}
                            >
                              Maintenance Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'inspectionCategory' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'inspectionCategory'}
                              direction={sortBy === 'inspectionCategory' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('inspectionCategory')}
                            >
                              Inspection Category
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'assemblies' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'assemblies'}
                              direction={sortBy === 'assemblies' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('assemblies')}
                            >
                              Assemblies
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'recurrence' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'recurrence'}
                              direction={sortBy === 'recurrence' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('recurrence')}
                            >
                              Recurrence
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'nextDueDate' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'nextDueDate'}
                              direction={sortBy === 'nextDueDate' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('nextDueDate')}
                            >
                              Next Due At
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'remainingLive' ? sortOrder : false}>
                            <TableSortLabel
                              active={sortBy === 'remainingLive'}
                              direction={sortBy === 'remainingLive' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('remainingLive')}
                            >
                              Remaining (Live)
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'numChecks' ? sortOrder : false} align="right">
                            <TableSortLabel
                              active={sortBy === 'numChecks'}
                              direction={sortBy === 'numChecks' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('numChecks')}
                            >
                              # Checks
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sortDirection={sortBy === 'numWorkOrders' ? sortOrder : false} align="right">
                            <TableSortLabel
                              active={sortBy === 'numWorkOrders'}
                              direction={sortBy === 'numWorkOrders' ? sortOrder : 'asc'}
                              onClick={() => handleSortRequest('numWorkOrders')}
                            >
                              # Work Orders
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">Loading...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : sortedSchedulesForActiveTab.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {hasActiveFilters
                                  ? 'No AMP schedules match the current filters in this category.'
                                  : `No AMP schedules in ${inspectionCategoryLabel(activeTab)}. Use "Create AMP Schedule" to add one.`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedSchedulesForActiveTab.map((schedule) => (
                            <TableRow
                              key={schedule._id}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleEdit(schedule)}
                            >
                              <TableCell sx={{ fontFamily: 'monospace' }}>{getAmpScheduleNumber(schedule)}</TableCell>
                              <TableCell>{schedule.title}</TableCell>
                              <TableCell sx={{ maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {schedule.description || '—'}
                              </TableCell>
                              <TableCell>{schedule.aircraftId?.registration || 'Unknown'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={schedule.maintenanceType === 'LINE_INSPECTION' ? 'Line' : 'Base'}
                                  size="small"
                                  color={schedule.maintenanceType === 'LINE_INSPECTION' ? 'primary' : 'secondary'}
                                />
                              </TableCell>
                              <TableCell>{inspectionCategoryLabel(schedule.inspectionCategory)}</TableCell>
                              <TableCell>{formatAssemblies(schedule)}</TableCell>
                              <TableCell>{formatRecurrence(schedule)}</TableCell>
                              <TableCell>
                                {(() => {
                                  const entries = getOrderedNextDueEntries(schedule);
                                  if (!entries.length) return <Typography variant="body2" component="div">—</Typography>;
                                  return entries.map((entry, idx) => {
                                    let displayText = entry.text;
                                    if (entry.nextDueDateLow && entry.nextDueDateHigh) {
                                      const lo = displayTimezone ? formatDateWithTimezone(entry.nextDueDateLow, displayTimezone) : entry.nextDueDateLow;
                                      const hi = displayTimezone ? formatDateWithTimezone(entry.nextDueDateHigh, displayTimezone) : entry.nextDueDateHigh;
                                      displayText = `Between ${lo} – ${hi}`;
                                    } else if (entry.nextDueDate) {
                                      displayText = displayTimezone ? formatDateWithTimezone(entry.nextDueDate, displayTimezone) : entry.text;
                                    }
                                    return entry.tooltip ? (
                                      <Tooltip key={`due-${idx}-${displayText}`} title={entry.tooltip} arrow placement="top">
                                        <Typography variant="body2" component="div" sx={{ lineHeight: 1.6, cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                                          {displayText}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Typography key={`due-${idx}-${displayText}`} variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                                        {displayText}
                                      </Typography>
                                    );
                                  });
                                })()}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const entries = getOrderedRemainingEntries(schedule);
                                  if (!entries.length) return <Typography variant="body2" component="div">—</Typography>;
                                  return entries.map((entry, idx) => (
                                    entry.tooltip ? (
                                      <Tooltip key={`rem-${idx}-${entry.text || ''}`} title={entry.tooltip} arrow placement="top">
                                        <Typography
                                          variant="body2"
                                          component="div"
                                          sx={{ lineHeight: 1.6, cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
                                        >
                                          {entry.text}
                                        </Typography>
                                      </Tooltip>
                                    ) : (
                                      <Typography key={`rem-${idx}-${entry.text || ''}`} variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                                        {entry.text}
                                      </Typography>
                                    )
                                  ));
                                })()}
                              </TableCell>
                              <TableCell>{schedule.ampChecks?.length || 0}</TableCell>
                              <TableCell>{schedule.workOrders?.length || 0}</TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <ScheduleActionButtons
                                  schedule={schedule}
                                  permissions={permissions}
                                  onEdit={handleEdit}
                                  onCreateIntimation={handleCreateIntimation}
                                  onDelete={handleDelete}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ))}

          {/* Create/Edit Dialog */}
          <CreateEditAMPScheduleDialog
            open={openDialog}
            onClose={handleDialogClose}
            onSuccess={handleDialogSuccess}
            schedule={selectedSchedule}
            aircraft={aircraft}
            initialAircraftId={selectedAircraftId || ''}
            initialInspectionCategory={activeTab}
            lockAircraftAndCategory={lockCreateContext}
            displayTimezone={displayTimezone}
            onProofDocumentsChange={handleProofDocumentsChange}
            canUploadProof={permissions.canEditAMPSchedule}
          />
        </>
      )}

      {/* Create Intimation Dialog */}
      <CreateIntimationFromAMPScheduleDialog
        open={createWOOpen}
        onClose={handleCloseCreateWODialog}
        onSuccess={handleCreateIntimationSuccess}
        ampSchedule={selectedScheduleForWO}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete the AMP schedule "{scheduleToDelete?.title}"?
            {scheduleToDelete?.workOrders && scheduleToDelete.workOrders.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                This schedule has {scheduleToDelete.workOrders.length} work order(s) created from it.
                Deleting the schedule will not delete the work orders.
              </Typography>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMPSchedules;
